import { Typography } from '@telavita-core/react-design-kit'
import React, { useCallback, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import MedicalRecordsService from 'services/medicalRecords'
import { MedicalRecordFormData } from '../../medicalRecordSchema'
import { Option } from '../../types/option'
import { OptionDetailsSelect } from '../OptionDetailsSelect'
import { SearchMultiSelect } from '../SearchMultiSelect'
import * as S from './styles'

interface SelectStatusOption {
  id: string
  label: string
}

const typesStatusOptions: SelectStatusOption[] = [
  { id: 'REFERRED', label: 'Referido' },
  { id: 'HYPOTHESIS', label: 'Hipótese' },
  { id: 'CONFIRMED', label: 'Confirmado' },
  { id: 'DONE', label: 'Encerrado' },
]

interface SelectedOption extends Option {
  id: number
  status: SelectStatusOption
  cidCode: string
  description: string
  diagnostic_hypothesis_medical_record_form_id: number
}

export function DiagnosticHypothesesSelect(): JSX.Element {
  const [
    diagnosticHypothesesOptions,
    setDiagnosticHypothesesOptions,
  ] = useState([])
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<MedicalRecordFormData>()
  const diagnosticHypothesesSelected = watch('diagnostic_hypotheses') || []
  const hasDiagnosticHypothesesSelected = diagnosticHypothesesSelected.length > 0

  const handleChangeValue = (value: SelectedOption[]) =>
    setValue('diagnostic_hypotheses', value)

  function handleRemoveSelectedOption(optionId: number) {
    const options = diagnosticHypothesesSelected.filter(
      (op) => op.id !== optionId
    )

    handleChangeValue(options)
  }

  const getDiagnosticHypotheses = useCallback(
    async (query: string, page: number, isSearch = false) => {
      const result = await MedicalRecordsService.listDiagnosticHypotheses(
        query,
        page
      )
      const options = result.diagnosticHypotheses.map((hypothesis) => {
        return {
          id: hypothesis.id,
          name: `${hypothesis.description} (CID ${hypothesis.cidCode})`,
          cidCode: hypothesis.cidCode,
          description: hypothesis.description,
        } as SelectedOption
      })

      setDiagnosticHypothesesOptions((state) => {
        if (isSearch) return options

        return [...state, ...options]
      })
      return result
    },
    []
  )

  return (
    <S.DiagnosticHypothesesContainer>
      <Controller
        control={control}
        name='diagnostic_hypotheses'
        render={({ field }) => (
          <SearchMultiSelect
            {...field}
            selectedOptions={diagnosticHypothesesSelected}
            onChange={field.onChange}
            options={diagnosticHypothesesOptions}
            label='Hipótese(s) Diagnóstica(s) *'
            id='diagnostic-hypotheses'
            description='Se não houver, digite e/ou selecione “nenhum(a)” para adicionar.'
            floatingLabel='Digite e/ou selecione para adicionar'
            inputPlaceholder='Digite e/ou selecione para adicionar'
            emptyOptionsMessage='Nenhum resultado. Verifique sua digitação.'
            errorMessage={errors.diagnostic_hypotheses?.message}
            loadOptions={getDiagnosticHypotheses}
          />
        )}
      />
      {hasDiagnosticHypothesesSelected && (
        <S.SelectedOptionsContainer>
          <S.SelectedOptionsTitle>
            <Typography variant='content3' italic>
              Adicionadas até agora:
            </Typography>
            <hr />
          </S.SelectedOptionsTitle>
          {diagnosticHypothesesSelected.map((option, index) => {
            const shouldEnableRemoveButton =!option.diagnostic_hypothesis_medical_record_form_id
            return (
              <Controller
                key={option.id}
                control={control}
                name={`diagnostic_hypotheses.${index}.status`}
                render={({ field, fieldState: { error } }) => (
                  <OptionDetailsSelect
                    {...field}
                    id={option.id}
                    nameId='diagnostic-hypotheses-select-option'
                    options={typesStatusOptions}
                    onChange={(value) => field.onChange(value)}
                    name={option.name}
                    messageError={error?.message}
                    onRemoveOption={shouldEnableRemoveButton && handleRemoveSelectedOption}
                    placeholder='Status'
                  />
                )
                }
              />
            )
          })}
        </S.SelectedOptionsContainer>
      )}
    </S.DiagnosticHypothesesContainer>
  )
}
